import { option, readonlyRecord, record, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import cookie from 'js-cookie';
import { z } from 'zod';
import { QUERY_PARAM_GA4_LINKER } from '../../constants';

const linkerDelimiter = '*';
const linkerValidityInMinutes = 30;

const getGA4Cookies = () =>
    pipe(
        cookie.get(),
        record.filterMapWithIndex((key, value) => {
            if (key.startsWith('_g')) {
                return option.some(value);
            }
            return option.none;
        })
    );

interface GenerateLinkerValueProps {
    ga4Cookies: Record<string, string>;
    currentTimestamp: number;
}

export const generateLinkerValue = ({ ga4Cookies, currentTimestamp }: GenerateLinkerValueProps) => {
    if (record.isEmpty(ga4Cookies)) {
        return null;
    }
    const cookiesString = JSON.stringify(ga4Cookies);

    return `${currentTimestamp}${linkerDelimiter}${cookiesString}`;
};

export const generateGA4LinkerValue = (currentTimestamp: number) => {
    const ga4Cookies = getGA4Cookies();

    return generateLinkerValue({ ga4Cookies, currentTimestamp });
};

interface ParseLinkerDataProps {
    linker: string;
    currentTimestamp: number;
}

/* Returns valid parsed object, null when linker is invalid or throw when query is corrupted */
export const parseLinkerData = ({ linker, currentTimestamp }: ParseLinkerDataProps): Record<string, string> | null => {
    const [timestamp, ga4Cookies] = linker.split(linkerDelimiter);
    const originTimestamp = parseInt(timestamp, 10);

    const isTimestampValid =
        !Number.isNaN(originTimestamp) && currentTimestamp - originTimestamp < linkerValidityInMinutes * 60 * 1000;

    return isTimestampValid ? JSON.parse(ga4Cookies) : null;
};

interface CheckAndSetGA4CookiesProps {
    contextWindow: Window;
    currentTimestamp: number;
    onError: (error: Error) => void;
}

const getBaseDomain = (hostname: string) => {
    const parts = hostname.split('.').filter(Boolean);
    if (parts.length > 2) {
        return parts.slice(parts.length - 2).join('.');
    }
    return hostname;
};

export const preserveGA4Cookies = async ({
    contextWindow,
    currentTimestamp,
    onError,
}: CheckAndSetGA4CookiesProps): Promise<void> => {
    try {
        const windowUrl = new URL(contextWindow.location.href);
        const queryParams = new URLSearchParams(windowUrl.search);
        const domain = getBaseDomain(contextWindow.location.hostname);

        const linker = queryParams.get(QUERY_PARAM_GA4_LINKER);
        if (linker == null) {
            return;
        }

        const ga4SessionValues = parseLinkerData({ linker, currentTimestamp });
        if (ga4SessionValues == null) {
            return;
        }
        const currentGa4Cookies = getGA4Cookies();

        const areCookiesAlreadySet = readonlyRecord.getEq(string.Eq).equals(ga4SessionValues, currentGa4Cookies);
        if (areCookiesAlreadySet) {
            return;
        }

        record.toArray(ga4SessionValues).forEach(([key, value]) => {
            cookie.set(key, value, {
                expires: 5,
                domain: `.${domain}`,
            });
        });

        // Give cookies 1 sec to settle prior to GTM script load
        await new Promise<void>((resolve) => {
            contextWindow.setTimeout(() => {
                resolve();
            }, 1000);
        });
    } catch (err) {
        onError(err);
    }
};

interface PurchaseEventLoggedCookieProps {
    contextWindow: Window;
    reservationGroupId: string;
}

const PURCHASE_EVENT_COOKIE_NAME = 'MEWS_ga_purchase';

export const purchaseEventCookieSchema = z.record(z.boolean());

const getPurchaseEventCookie = () => {
    try {
        const value = cookie.get(PURCHASE_EVENT_COOKIE_NAME);

        return value != null ? purchaseEventCookieSchema.parse(JSON.parse(value)) : {};
    } catch (err) {
        return {};
    }
};

export const isPurchaseEventTriggered = (reservationGroupId: string): boolean => {
    const cookieValue = getPurchaseEventCookie();

    return cookieValue[reservationGroupId] ?? false;
};

export const setPurchaseEventTriggered = ({ contextWindow, reservationGroupId }: PurchaseEventLoggedCookieProps) => {
    const domain = getBaseDomain(contextWindow.location.hostname);
    const cookieValue = getPurchaseEventCookie();
    const value = JSON.stringify({ ...cookieValue, [reservationGroupId]: true });

    cookie.set(PURCHASE_EVENT_COOKIE_NAME, value, {
        expires: 5,
        domain: `.${domain}`,
    });
};
